/*!
 * EXPLOIT — launch page (Book One of The Universe). Built to the imported spec
 * (task-tracker/exploit-launch-site/SITE-SPEC.md) with OUR adaptations
 * (README-ADAPTATIONS.md); byline "Justin K Kazmierczak".
 *
 * ══ 2026-08-21 FOUNDER DIRECTIVE — THIS PAGE NO LONGER SELLS THE BOOK ══
 * Verbatim: "Remove any and all refrences to pre-order… The exploit microsite
 * now pushes excuslively to getting your free sample chapters and getting
 * people on the list hwen it launches. No one has preordered at all. Remove any
 * refrences to the date of the book's release and change to coming soon."
 *
 * What that means here, and why:
 *  - ONE call to action, everywhere on the surface: the free opening (the
 *    Foreword, the Prologue and Chapters 1–7) through the §22 capture, which is
 *    also how a reader gets on the list for launch. ctaPrimary() therefore
 *    points at §22 (#read-the-opening) — there is nothing else to ask for.
 *  - NO price, NO offer, NO checkout on this page. The Named Edition — the
 *    first-edition hardback, the certificate of authenticity, and the registry
 *    of The Named — is a PREORDER-ONLY exclusive, so it is off the public page
 *    entirely (founder ruling R2, 2026-08-21). It survives UNEDITED inside the
 *    hidden wizard (app/elements/egt.exploit.reserve.js), ready for launch day.
 *  - NO release date. "Coming soon" is the posture, which puts this surface
 *    back under the standing agents.md §4 "no published dates" rule. This
 *    SUPERSEDES the 2026-07-22 "the ship window IS STATED, DELIBERATELY" ruling
 *    that used to occupy this header. That reasoning — 16 CFR 435.2(a)(1)(ii)
 *    inserting a thirty-day default, the delay-notice path, the chargeback
 *    clock — is entirely about a PAID physical good with money already taken.
 *    Nothing is sold from this page now, so no statutory shipping clock is
 *    running and there is no date to state. If the book is ever sold from here
 *    again, the stated window comes BACK with it: read
 *    task-tracker/research/commerce-preorder-terms.md FIRST.
 *  - "No countdowns, no fake stock, no price games" still holds, and is now
 *    trivially true.
 *
 * Removed by this directive (all recoverable from git if the founder wants any
 * of it back): §04b "The Offer at a Glance", §18 "The first readers", §19 "The
 * Named Edition", §20 "Preorder only — it does not come back", the Offer block
 * in the JSON-LD, and the preorder/ship-date FAQ entries. §19's `id="read"` is
 * PRESERVED on its replacement — it is the fragment emailed as `readUrl`
 * (app/helpers/exploit.reserve.js) and must keep landing somewhere real.
 *
 * The free opening is delivered as soon as the address is confirmed — the §22
 * copy says "right away". (An older note here claimed "a few days"; that was
 * stale, not the copy.)
 *
 * The albums are FREE for everyone and always were — never a reserve perk, so
 * nothing about them changes. NO money-back guarantee, NO value-stack
 * arithmetic. Renders inside the EGT shell (S.page).
 *
 * DARK until launch: gated by app/helpers/egt.exploit.config.js (the /exploit
 * route 404s while off). The page encodes the book's dark→hope arc into the
 * scroll (a --descent var driven by app/public/src/egt.exploit.js). Every motion
 * is additive over a fully-present base (reduced-motion + no-JS safe).
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.exploit",
  title: "EXPLOIT launch page",
  description: "Book One of The Universe — the immersive launch page for the novel.",
  supportServer: true
});
var S = require("../helpers/egt.sections.js");
/* the SHARED per-track ROW builder — the SAME rows the per-album detail pages
 * render (title + "Character:" POV + feeling + the fs.existsSync-gated preview
 * player), so the soundtrack tabs here can never drift from /entertainment. */
var TR = require("../helpers/egt.trackrow.js");

/* ---- small copy helpers (semantic + reveal hooks) ----
 * Every one runs its copy through S.priceToken, so ANY string containing
 * "$75 + shipping" gets the unbreakable span without the call site having to
 * remember. That is the point: the previous pass guarded the CTA by hand and
 * left the micro label, the price spans and the body prose splitting mid-token
 * at phone widths. Applying it at the helper makes the guarantee structural.
 * S.priceToken passes non-matching strings and node arrays straight through. */
function micro(text, mod) { return { n: "p", c: "egt-exp__micro" + (mod ? " " + mod : ""), i: S.priceToken(text) }; }
function eyebrow(text) { return { n: "p", c: "egt-exp__eyebrow egt-exp-reveal", i: S.priceToken(text) }; }
function h(text, mod) { return { n: "h2", c: "egt-exp__h" + (mod ? " " + mod : "") + " egt-exp-reveal", i: S.priceToken(text) }; }
function body(nodes) { return { n: "p", c: "egt-exp__body egt-exp-reveal", i: S.priceToken(nodes) }; }

/* THE ONE CALL (2026-08-21 founder directive — see the file header). Every
 * primary CTA on this page asks for exactly one thing: the free opening
 * chapters, which is also how a reader gets on the list for launch. It points
 * at §22's own capture (#read-the-opening) rather than off the page, so the
 * reader says yes without leaving the story. /exploit/free is the SAME offer as
 * a standalone landing page, and is where paid traffic lands.
 * There is deliberately no second, competing ask anywhere on this surface.
 *
 * ONE VERB, ONE STRING (2026-08-21). Four different phrasings of this single ask
 * shipped simultaneously — nav "Read Exploit free", hero "Read the opening
 * free", dock label "Exploit: read the opening free", dock button "Get the
 * chapters →". They now follow one rule, and it has exactly two roles:
 *   · every LINK that asks for the chapters says "Read the opening free" —
 *     the nav CTA, the hero, §10, §19, §24, and the sticky dock's button
 *     (app/public/src/egt.exploit.js).
 *   · the ONE FORM BUTTON, §22's capture submit, says "Send me the opening" —
 *     because at that point you are not reading yet, you are asking for it, and
 *     a submit button that says "Read" would lie about what the click does.
 * Nothing else on the surface asks for anything. If you add a CTA, use the
 * string; if you find a fifth phrasing, it is a bug. */
function ctaPrimary(label) {
  return { n: "a", c: "egt-exp__cta egt-exp__cta--primary", href: "#read-the-opening",
    "data-exp-cta": "free-chapters", "data-exp-magnetic": "true",
    i: { n: "span", i: S.priceToken(label || "Read the opening free") } };
}
function ctaGhost(label, href) {
  return { n: "a", c: "egt-exp__cta egt-exp__cta--ghost", href: href || "#read-the-opening",
    i: { n: "span", i: label } };
}

/* ===================================================================
 * SECTION 01 — Cinematic Hero: The Leap  ★  (ONE title moment)
 *
 * Q19 — founder ruling 2026-07-22: §00 (the black threshold / curtain card:
 * label + wordmark image + "Enter ↓") was DELETED and merged into this hero.
 * The two were consecutive full-bleed brand statements that both said "this is
 * Exploit", and the title-card art rendered TWICE, one screen apart. There is
 * now exactly ONE title moment — the title card + the credit line over the hero
 * image — and the reader falls straight into the §02 hook. Nothing from §00
 * survived because §00 held nothing this section did not already say: its
 * "Book One of The Universe" label is the first clause of the genre line below,
 * its wordmark IS this section's title card, and its "Enter ↓" is this
 * section's "Scroll ↓". This reclaims a full screen before the first offer.
 * The hero keeps its own `exp-hero` id (the dock's scroll threshold) and gains
 * `data-exp-top` so it is still identifiable as the top beat now that the
 * `exp-top` element is gone.
 * ================================================================= */
function sec01() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--01 egt-exp__hero", id: "exp-hero", "data-exp-top": "true", "data-exp-sec": "01",
    i: [
      /* the background is currently a still (exploit-hero-bg); the founder
       * plans to replace this layer with a video, with the title card (below)
       * sitting transparently on top of it — swap the <img> for a <video> here
       * once that asset exists, same .egt-exp__hero-media layer/z-index. */
      { n: "div", c: "egt-exp__hero-media", "aria-hidden": "true", "data-exp-parallax": "0.2",
        i: { n: "img", c: "egt-exp__hero-img", src: "/imgs/exploit/exploit-hero-bg.jpg",
          alt: "", loading: "eager", fetchpriority: "high", decoding: "async" } },
      /* DELIBERATELY NO scrim layer over the hero art: agents.md §4 forbids
       * dimming media. The art is near-black wherever the copy sits, so every
       * line clears WCAG AA unaided. AA floor is 4.5:1 for all of them except
       * the h2, which is large text at 3:1.
       *
       * Two methods, because they disagree and both are legitimate. ENVELOPE =
       * the worst pixel over the whole Ken Burns (scale 1.04→1.14) × parallax
       * range, i.e. the worst instant the animation can ever reach. INSTANT =
       * the frame as currently rendered. Envelope is the conservative one and is
       * what these figures quote first. (The parallax term is swept at a fixed
       * ±60px. That is the chosen sweep width, NOT the implementation's
       * reachable range — do not read it as a bound on the latter. If you need
       * the real range, derive it from the guard in app/public/src/egt.exploit.js,
       * which is `r.bottom < -vh`: the layer keeps updating for a full extra
       * viewport after it has scrolled off the top, so the range runs wider than
       * the intuitive "until the media clears the viewport" answer, and on large
       * desktop viewports it does exceed the sweep. Whether the sweep fully
       * covers the legible range at unusually tall viewports is an OPEN
       * QUESTION — tracked as H20 in task-tracker/29-MASTER-TASK-LIST.md. Read
       * the ticket before relying on these figures at such a viewport.)
       *              h2      sub     genre   trust   scroll
       *   1440  env  9.89    7.83    8.26    6.70    7.88
       *         inst 11.03   8.03    8.26    6.75    8.37
       *    500  env  12.63   7.23    7.13    6.93    8.20
       *         inst 12.82   7.53    8.35    7.27    8.44
       *    390  env  11.55   7.03    6.32    7.14    8.30
       *         inst 12.63   7.67    8.44    7.27    8.47
       * Surface minimum: 6.32 (envelope) / 6.75 (instant). Do NOT assert which
       * line is "the tightest" — the ordering is method-dependent (at 390 it is
       * genre by envelope but trust by instant), so that claim is not
       * reproducible unless the method is stated with it.
       *
       * Backdrops: per-line worst backdrop reaches rgb(67,67,67) by envelope,
       * rgb(60,60,60) by instant. The brightest pixel in exploit-hero-bg.jpg as
       * a whole is rgb(131,95,35) — full-resolution scan of all 3,686,400
       * pixels; a strided scan under-reports it — but it sits top-right and
       * never lands behind copy within the swept envelope.
       *
       * These are POST-reflow numbers. Moving the scroll cue into normal flow
       * lengthened .egt-exp__hero-inner, which slid every line onto different
       * pixels of the art — re-measure the whole set after ANY layout change
       * here, not just the line you touched. (Measure per width with a FRESH
       * page load: vw-based values like --egt-pad go stale if an iframe is
       * resized across tiers rather than reloaded. G-18.)
       *
       * Re-checking this correctly means two things, and BOTH have already been
       * got wrong on this surface:
       * 1. Measure the EFFECTIVE alpha, not the declared colour — that is the
       *    colour's own alpha × the MINIMUM across all @keyframes opacity stops
       *    × any CSS-transition end state × every ancestor's opacity. Measuring
       *    the animation's PEAK hid a real failure: .egt-exp__micro--scroll ran
       *    expFloat down to opacity .5 over a .72 colour, so its true worst case
       *    was .36 alpha / 2.81:1, not the 8.47:1 the peak reported. (Note the
       *    transition term: .egt-exp-reveal and .egt-exp__kin-i animate opacity
       *    by transition rather than keyframes. Both settle at 1 so they are
       *    harmless today, but a keyframes-only sweep would not see them.)
       * 2. Sample the pixels ACTUALLY behind the line, not the artwork you
       *    assume is there. The same scroll cue was painted through the gold
       *    trust line at every width below 695px — where its real backdrop was
       *    a glyph, 1.76:1, while the artwork behind that same spot still
       *    measured 8.44:1. Fixed by laying the cue out in NORMAL FLOW
       *    UNCONDITIONALLY, at every width: there is no breakpoint for it, and
       *    deliberately so — 695px is the width at which the trust SENTENCE
       *    stops wrapping, so any media query pinned to it would silently stop
       *    working the moment that copy is edited. See the layout note on
       *    .egt-exp__micro--scroll in egt.exploit.scss. */
      { n: "div", c: "egt__shell egt-exp__hero-inner",
        i: [
          micro("Book One of The Universe · A cinematic spiritual thriller · A novel by Justin K Kazmierczak", "egt-exp__micro--genre"),
          /* the real book title-card art (alpha-transparent lockup), in place of
           * a typographic title here — the tagline below carries the hook.
           * Wrapped in the page's ONE <h1> so alt="Exploit" IS the heading
           * (SEO wave 2C-1, 2026-10-09). No visually hidden text; the h1 has
           * zero margin of its own, so the layout is unchanged. */
          { n: "h1", c: "egt-exp__hero-h1",
            i: { n: "img", c: "egt-exp__hero-title-card egt-exp-reveal", src: "/imgs/exploit/exploit-title-card.avif",
              alt: "Exploit", loading: "eager", decoding: "async" } },
          { n: "h2", c: "egt-exp__h egt-exp__h--hero egt-exp-reveal",
            i: [ "They came to break one man. ", { n: "span", c: "egt-exp__nowrap", i: "He was never the only one standing." } ] },
          { n: "p", c: "egt-exp__sub egt-exp-reveal",
            i: "A man in a dark coat walks off the tallest building at sunset — smiling — and opens a glowing hand as he falls. An ancient order murdered his whole family to break him. It only scattered a war across everyone who loves him." },
          /* ONE CTA, not two. This row used to be a preorder button plus a
           * "Read the opening free →" ghost. With the preorder gone the two
           * would say the same thing twice, so the hero now carries the single
           * ask and nothing to weigh it against. (The "Scroll ↓" line below is
           * the invitation to keep reading; the sticky dock repeats the ask
           * once the hero is past.) */
          { n: "div", c: "egt-exp__cta-row",
            i: ctaPrimary("Read the opening free") },
          micro("Book One of The Universe · the Foreword, the Prologue and Chapters 1–7, free · the book is coming soon", "egt-exp__micro--trust"),
          micro("Scroll ↓", "egt-exp__micro--scroll")
        ] }
    ] };
}

/* ===================================================================
 * STAT BAND (S1) — "by the numbers", between the hero (§01) and the hook
 * (§02). A portfolio-style beat: big figures that COUNT UP on scroll-into-view
 * (app/public/src/egt.exploit.js armCountUp → data-exp-count), each with a
 * crisp uppercase label. Founder 2026-07-25: 50 chapters · 202,521 words "and
 * counting" · ~750 pages · 8 main characters.
 *
 * Progressive enhancement: the FINAL, formatted figure ("202,521") is authored
 * straight into the HTML `i:` text, so with no JS or under prefers-reduced-motion
 * the real numbers are already present and static — the client only animates
 * from 0 up to them and restores the exact authored string on the last frame.
 *
 * NUMBERING: this is a HALF-height band (padding-block only, well under the
 * 0.72×vh threshold buildIndices() numbers by) AND it carries data-exp-noindex,
 * so it is neither numbered nor counted and the existing 02,03,… numeral
 * sequence is unchanged. Its numbers use their OWN classes (never .egt-exp__h /
 * __pull / __excerpt-line / __hook-line--beat), so armKinetic() never rebuilds
 * their textContent (would have destroyed the count-up target). */
function statTile(s) {
  var num = { n: "span", c: "egt-exp__stat-num", "data-exp-count": s.count, i: s.display };
  if (s.suffix) { num["data-exp-count-suffix"] = s.suffix; }
  var kids = [ num, { n: "span", c: "egt-exp__stat-label", i: s.label } ];
  if (s.note) { kids.push({ n: "span", c: "egt-exp__stat-note", i: s.note }); }
  return { n: "div", c: "egt-exp__stat egt-exp-reveal", i: kids };
}
function secStats() {
  var STATS = [
    { display: "202,521", count: "202521", label: "Words", note: "and counting" },
    { display: "750+", count: "750", suffix: "+", label: "Pages" },
    { display: "50", count: "50", label: "Chapters" },
    { display: "8", count: "8", label: "Main characters" }
  ];
  return { n: "section", c: "egt-exp__sec egt-exp__sec--stats egt-exp__center",
    "data-exp-sec": "stats", "data-exp-noindex": "true",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("By the numbers"),
        { n: "div", c: "egt-exp__stats", i: STATS.map(statTile) } ] } };
}

/* ===================================================================
 * SECTION 02 — The Hook / Logline
 * ================================================================= */
function sec02() {
  var lines = [
    { n: "p", c: "egt-exp__hook-line egt-exp__hook-line--lead egt-exp-reveal", i: "I'll be honest: this book goes to terrible places. On purpose." },
    { n: "p", c: "egt-exp__hook-line egt-exp-reveal", i: "An innocent man, marked by the oldest dark power in the world. Everyone he loves — taken. He's left alive to feel it." },
    { n: "p", c: "egt-exp__hook-line egt-exp-reveal", i: "But every system has a flaw. A touch of magic, long forgotten. A cheat code buried under everything." },
    { n: "p", c: "egt-exp__hook-line egt-exp__hook-line--beat egt-exp-reveal", i: "Eight souls. One secret war. One reveal." },
    { n: "p", c: "egt-exp__hook-line egt-exp-reveal", i: [ "Let's call it an ", { n: "span", c: "egt-exp__word-green", "data-exp-underline": "true", i: "Exploit." } ] }
  ];
  /* Background: war-green-sphere (the missile dying against the sphere of
   * green) — founder ruling 2026-07-22 (Q18); §02 had no background at all.
   * Same media layer as §01's hero (.egt-exp__sec-media / __sec-img, parallax),
   * and DELIBERATELY NO scrim / overlay: agents.md §4 forbids dimming the
   * artwork. Contrast comes from the frosted .egt-glass card the hook now sits
   * in — the same pattern §06/§07 now use via mediaSection(). The /imgs route
   * serves the .avif sibling to browsers that accept it. */
  return { n: "section", c: "egt-exp__sec egt-exp__sec--02", "data-exp-sec": "02",
    i: [
      { n: "div", c: "egt-exp__sec-media", "aria-hidden": "true", "data-exp-parallax": "0.08",
        i: { n: "img", c: "egt-exp__sec-img", src: "/imgs/exploit/war-green-sphere.jpg", style: bgFocus("war-green-sphere"),
          alt: "", loading: "lazy", decoding: "async" } },
      { n: "div", c: "egt__shell egt-exp__sec-inner",
        i: { n: "div", c: "egt-glass egt-exp__hook", i: lines } }
    ] };
}

/* ===================================================================
 * SECTION 03 — The Severance (Inciting Blow)
 * ================================================================= */
/* §03 — no board render exists in the novel repo yet, so this is a cold
 * type-on-dark beat (honest; no fabricated art). The "one unmarked photo" is
 * evoked in a CSS motif, not a fake image. */
function sec03() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--03 egt-exp__dark", "data-exp-sec": "03",
    i: { n: "div", c: "egt__shell egt-exp__sec-inner",
      i: [
        eyebrow("THE ORDER"),
        h("Kill everyone he loves. Leave him alive underneath it."),
        body("An order older than the century decides Jonathan Avery Karros is dangerous — and that a martyr is useless. So they don't come for him. They come for everyone he belongs to. A country away, one photograph on their board has no mark across it. That's where the war they started turns around."),
        { n: "p", c: "egt-exp__spark egt-exp-reveal", "aria-hidden": "true", i: "▪" },
        micro("A scattered family is harder to erase than one man.", "egt-exp__micro--caption")
      ] } };
}

/* ===================================================================
 * SECTION 04 — "The First Story of the Universe"
 * ================================================================= */
function sec04() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--04 egt-exp__calm", "data-exp-sec": "04",
    i: { n: "div", c: "egt__shell egt-exp__center",
      i: [
        eyebrow("BOOK ONE"),
        h("This is Book One. The door, not the room."),
        body([ { n: "em", i: "Exploit" }, " is Book One of ", { n: "strong", i: "The Universe" }, " — but it's a complete story you can start and finish right here. The world underneath it is older and deeper than this one war: loss is real, no one falls alone, and the villains you were handed are only the opening act. You don't need to know any of that to fall in. It's just there, underneath, for whenever you want to look closer." ]),
        { n: "div", c: "egt-exp__gold-line egt-exp-reveal", "aria-hidden": "true" }
      ] } };
}

/* SECTION 04B — "The Offer at a Glance" (id=offer) — REMOVED 2026-08-21.
 * It was the Named Edition offer card: the price, the certificate of
 * authenticity and the registry, with a preorder button. All of that is now a
 * preorder-only exclusive and lives ONLY in the hidden wizard (founder ruling
 * R2). Nothing replaces it here: the free-opening capture (§22) already sits
 * two sections above, so the page asks once and then gets on with the story.
 * Nothing linked to #offer, so no fragment broke. Recover from git if the
 * founder wants the card back at launch.
 * ================================================================= */

/* full-bleed media section (real art background + frosted card + content).
 * agents.md §4: NO scrim / overlay / opacity-dim on the media. Contrast comes
 * from the frosted .egt-glass--dark card ONLY — the same shape sec02() uses over
 * war-green-sphere — which covers the copy column while the photograph breathes
 * around it. §06 (The Magic) and §07 (The War) get the IDENTICAL treatment
 * through this one helper, so the whole copy block (including §06's pull quote,
 * which white type cannot make legible on a bright photo unaided) sits inside
 * the card. §07's body + caption additionally take a stronger colour token — see
 * the .egt-exp__media-card rules in egt.exploit.scss: --bone-soft (.72) and
 * --bone-dim (.42) are too weak to sit on a bright photograph even behind the
 * blurred card, so the copy inside these cards uses opaque var(--bone) (the
 * ELEMENT's token is swapped in-scope; the site-wide --bone-* definitions are
 * untouched). Every line is AA-clear against the BRIGHTEST blurred patch of each
 * image, which is the conservative bound over the entire Ken Burns × parallax
 * range — Chrome-verified; see the H5 row in task-tracker/29-MASTER-TASK-LIST.md.
 * (The old .egt-exp__sec-scrim — a rgba(11,12,15,.5→.82) gradient — is removed;
 * it was the last §4 violation on the surface and it failed AA on 3 lines anyway.) */
/* Phone focus for the widescreen 4K backgrounds (desktop shows ~the whole
 * frame; a tall phone section shows a slice) — picked by eye from each frame. */
var BG_FOCUS = { "war-green-sphere": "50% 55%", "magic-the-light": "56% 45%", "loc-rooftop": "35% 50%" };
function bgFocus(src) {
  var k = String(src).replace(/^.*\//, "").replace(/\.[a-z]+$/, "");
  return "object-position:" + (BG_FOCUS[k] || "50% 50%");
}

function mediaSection(mod, img, dataSec, inner) {
  return { n: "section", c: "egt-exp__sec egt-exp__media-sec " + mod, "data-exp-sec": dataSec,
    i: [
      { n: "div", c: "egt-exp__sec-media", "aria-hidden": "true", "data-exp-parallax": "0.08",
        i: { n: "img", c: "egt-exp__sec-img", src: img, style: bgFocus(img), alt: "", loading: "lazy", decoding: "async" } },
      { n: "div", c: "egt__shell egt-exp__sec-inner",
        i: { n: "div", c: "egt-glass egt-glass--dark egt-exp__media-card", i: inner } }
    ] };
}

/* §05 — World & Lore (no location renders exist → type-driven, warming). */
function sec05() {
  var frags = [
    "An order that has hoarded a forgotten power for centuries.",
    "A warmth one man carries and cannot own — a plain green light that heals what it touches.",
    "And a place the world was pushed out of long ago, quietly waiting to be set right."
  ].map(function (t) { return { n: "p", c: "egt-exp__frag egt-exp-reveal", i: t }; });
  return { n: "section", c: "egt-exp__sec egt-exp__sec--05 egt-exp__center", "data-exp-sec": "05",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("THE WORLD"), h("Three times. Two worlds. One night they all come due."),
        { n: "div", c: "egt-exp__frags", i: frags },
        micro("You don't get it all explained. You get to live in it.", "egt-exp__micro--caption") ] } };
}

/* §06 — The Magic (real art: the green light in the cupped hand). */
function sec06() {
  return mediaSection("egt-exp__sec--06", "/imgs/exploit/magic-the-light.jpg", "06", [
    eyebrow("THE MAGIC"),
    h("A power he can't own — and won't use the way they expect."),
    body("The light in his hand doesn't belong to him. He didn't earn it and he can't keep it. It can thaw a grieving stranger's frozen heart. It can turn a direct hit into nothing. And the whole war turns on one question he keeps answering: not what he's able to do — what he refuses to become."),
    { n: "p", c: "egt-exp__pull egt-exp-reveal", i: "“Not the fire he could survive. The fire he could refuse to become.”" }
  ]);
}

/* §07 — The War (real art: the missile dying on the green sphere). */
function sec07() {
  return mediaSection("egt-exp__sec--07", "/imgs/exploit/loc-rooftop.jpg", "07", [
    eyebrow("THE WAR"),
    h("They released a war. He answered with a wall of light."),
    body("When the order becomes a campaign — a convoy torn apart, a city's lights bent to shield one man, a missile that dies against a sphere of green and leaves him standing — the power around him could end it all in a single stroke. And at the last instant, he hears one man scream for mercy. And he stops."),
    micro("The tip of the spear. Not the whole of it.", "egt-exp__micro--caption")
  ]);
}

/* §08 — The "First Villain" wink (type-only; a door left ajar). */
function sec08() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--08 egt-exp__dark egt-exp__center", "data-exp-sec": "08",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("A SMALL WARNING"),
        h("They think they're the last, greatest evil. They're only the opening act."),
        { n: "div", c: "egt-exp__door egt-exp-reveal", "aria-hidden": "true" },
        body("You already know who the villains are. That's the joke. Hand a reader the oldest secret order in the world and let them assume it's the top of the pyramid — then let the floor drop out. Every answer here opens a bigger door. Some of them don't close."),
        micro("No spoilers. That would ruin it.", "egt-exp__micro--caption") ] } };
}

/* §09 — The Eight Souls. Real portraits for the seven leads; the 8th is
 * withheld by count only (hard spoiler — never named or shown). */
var SOULS = [
  { name: "Jonathan Avery Karros", essence: "The showman who carries a warmth he never earned.", img: "soul-karros" },
  { name: "Reyna", essence: "His estranged sister. She turned on the assassin sent for her children — and put him to work.", img: "soul-reyna" },
  { name: "Ronan", essence: "His best friend, and more than he seems. He won't let a broken man fall.", img: "soul-ronan" },
  { name: "Janelle", essence: "His right hand. Fierce, funny, and immovable.", img: "soul-janelle" },
  { name: "Cole", essence: "An award-winning musician carrying a weight no one else could lift.", img: "soul-cole" },
  { name: "Carleton", essence: "A hired gun with a conscience — and a line he won't cross.", img: "soul-carleton" },
  { name: "Niko", essence: "The enemy's blunt instrument, with a conscience that won't stay dead.", img: "soul-niko" }
];
function soulCard(s) {
  return { n: "article", c: "egt-exp__soul egt-exp-reveal",
    i: [
      { n: "div", c: "egt-exp__soul-portrait",
        i: { n: "img", c: "egt-exp__soul-img", src: "/imgs/exploit/" + s.img + ".jpg",
          alt: s.name, loading: "lazy", decoding: "async" } },
      { n: "h3", c: "egt-exp__soul-name", i: s.name },
      { n: "p", c: "egt-exp__soul-essence", i: s.essence }
    ] };
}
function sec09() {
  var cards = SOULS.map(soulCard);
  cards.push({ n: "article", c: "egt-exp__soul egt-exp__soul--withheld egt-exp-reveal",
    i: [ { n: "div", c: "egt-exp__soul-portrait egt-exp__soul-portrait--coin", "aria-hidden": "true" },
      { n: "h3", c: "egt-exp__soul-name", i: "The eighth" },
      { n: "p", c: "egt-exp__soul-essence", i: "Withheld. You'll meet him where it matters." } ] });
  return { n: "section", c: "egt-exp__sec egt-exp__sec--09 egt-exp__dark", "data-exp-sec": "09",
    i: { n: "div", c: "egt__shell egt-exp__center",
      i: [ eyebrow("THE EIGHT SOULS"),
        h("A family scattered across a war — and refusing to fall alone."),
        { n: "div", c: "egt-exp__souls", i: cards },
        micro("They came to break one man. They never counted the hands reaching back.", "egt-exp__micro--caption") ] } };
}

/* §10 — Emotional throughline + CTA. */
function sec10() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--10 egt-exp__center", "data-exp-sec": "10",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("THE HEART OF IT"),
        h("When they burn down everything you love, what will you refuse to become to save it?"),
        body("This is not one man's revenge. It's a scattered family re-gathering in the dark — a sister who recruits her own would-be killer, friends who close ranks around a shattered man, an enemy who starts to feel something the dark swore it had burned out of him. Loss is real here. It is never the last word."),
        { n: "p", c: "egt-exp__pull egt-exp-reveal", i: "“As long as we're standing here with you, nothing gets through — without going through us first.”" },
        { n: "div", c: "egt-exp__cta-row", i: ctaPrimary("Read the opening free") },
        micro("Book One of The Universe.", "egt-exp__micro--trust") ] } };
}

/* full-screen single-line excerpt "title card". */
function excerpt(line, cite, mod) {
  return { n: "section", c: "egt-exp__sec egt-exp__excerpt " + (mod || ""), "data-exp-sec": "excerpt",
    i: { n: "div", c: "egt__shell egt-exp__center",
      i: [ { n: "p", c: "egt-exp__excerpt-line egt-exp-reveal", i: line },
        cite ? micro(cite, "egt-exp__micro--cite") : null ].filter(Boolean) } };
}
function sec11() { return excerpt("“Pain doesn't reveal character. Pain forges it.”", "from Exploit — Chapter Eight", "egt-exp__dark"); }

/* §12 — Genre & tone */
function sec12() {
  var chips = ["Cinematic", "Propulsive", "Hope-threaded"].map(function (t) {
    return { n: "span", c: "egt-exp__chip egt-exp-reveal", i: t }; });
  return { n: "section", c: "egt-exp__sec egt-exp__sec--12 egt-exp__center", "data-exp-sec": "12",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("WHAT YOU'RE HOLDING"),
        h("A cinematic spiritual thriller. Dark as it needs to be. Lit from underneath."),
        body("Propulsive and shot like a film — a severed head delivered to a boardroom in a cold steel case, a war run through hijacked national grids, an afterworld you can feel but never quite explain. Underneath all of it runs a stubborn thread of hope and design: the sense that even here, someone is keeping a plan you can't yet see."),
        { n: "div", c: "egt-exp__chips", i: chips },
        /* One quotable, verbatim-liftable entity + comp sentence (AI-EO): states
         * plainly what the book is, who wrote it, its place in the series, and the
         * comp authors readers use as reference points (Peretti / Dekker). This is
         * the disambiguating, citeable line the organic-reach research calls for. */
        micro("Exploit is a cinematic spiritual thriller by Justin K Kazmierczak — Book One of The Universe, for readers of Frank Peretti and Ted Dekker.", "egt-exp__micro--caption") ] } };
}

/* §13 — The Order dossier (diegetic file cards) */
function sec13() {
  var entries = [
    { k: "The Order.", v: "Older than the century. Descended from things older still. They believe patience is a religion." },
    { k: "The Leadership.", v: "Twelve who sit at the top of the pyramid — and struggle, quietly, between the want for power and the want to do good." },
    { k: "The 13th.", v: "The one who looks like ultimate power. He is the most enslaved seat of all — and there is a man inside him, weeping." }
  ].map(function (e) { return { n: "div", c: "egt-exp__file egt-exp-reveal",
    i: [ { n: "strong", c: "egt-exp__file-k", i: e.k }, { n: "span", c: "egt-exp__file-v", i: " " + e.v } ] }; });
  return { n: "section", c: "egt-exp__sec egt-exp__sec--13 egt-exp__dark egt-exp__center", "data-exp-sec": "13",
    i: { n: "div", c: "egt__shell",
      i: [ micro("FIELD NOTES — DO NOT DISTRIBUTE", "egt-exp__micro--stamp"),
        h("Peel one layer. Find a darker one."),
        { n: "div", c: "egt-exp__files", i: entries },
        micro("And behind them? That's for later books.", "egt-exp__micro--caption") ] } };
}

function sec14() { return excerpt("“The story's about to get worse before it's better. It always gets better.”", "from Exploit", "egt-exp__warm"); }

/* §15 — The Author (manifesto) */
function sec15() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--15 egt-exp__warm", "data-exp-sec": "15",
    i: { n: "div", c: "egt__shell egt-exp__author",
      i: [ eyebrow("WHY THIS STORY"),
        h("I wrote the book I needed to believe was true."),
        { n: "blockquote", c: "egt-exp__manifesto egt-exp-reveal",
          i: "I started this before the world got hard for everyone at once, and it only got truer after. People get an enormous amount thrown at them. Things get destroyed. What I wanted to put on the page is the thing I keep finding underneath the wreckage — that it is not all lost, that no one is falling as alone as they feel, and that there's a plan running under all of it we don't get to fully see. I open dark on purpose. The hope has to have something to shine against." },
        { n: "p", c: "egt-exp__signature egt-exp-reveal", i: "— Justin K Kazmierczak" } ] } };
}

/* §16 — The Universe: the larger vision */
function sec16() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--16 egt-exp__center", id: "exp-universe", "data-exp-sec": "16",
    i: { n: "div", c: "egt__shell egt-exp__author",
      i: [ eyebrow("THE UNIVERSE"),
        h("The world is older than this one war."),
        body("Exploit tells a complete story — you can begin and end it right here. But you'll feel the edges of something larger pressing in around it: something that doesn't announce itself, and won't explain all at once. Stay in the book. The rest reveals itself in its own time."),
        { n: "div", c: "egt-exp__gold-line egt-exp-reveal", "aria-hidden": "true" } ] } };
}

/* §17 — Visual gallery (real, spoiler-SAFE scenes only) */
function sec17() {
  var plates = [
    { img: "scene-carleton-fire", cap: "Old habits, and a fire at his back." },
    { img: "scene-reyna-family", cap: "Nobody touches my family." },
    { img: "war-green-sphere", cap: "A wall of light against the whole of it." },
    { img: "scene-cole-return", cap: "Pulled back onstage, out of a private grief." },
    { img: "scene-carleton-killer", cap: "A hired gun with a line he won't cross." },
    { img: "loc-rooftop", cap: "The edge, and the whole city burning below." },
    { img: "scene-reyna-comes", cap: "The one who comes when it's worst." },
    { img: "scene-ronan-board", cap: "A seat at the table, and a second life." },
    { img: "loc-author-space", cap: "A threshold of light, felt more than seen." },
    { img: "loc-hideout", cap: "Old tech, kept secrets, and an ancient mind." },
    { img: "scene-janelle-hand", cap: "The hand that holds his world together." },
    { img: "scene-niko-coin", cap: "A coin — and the weight of who owns it." }
  ].map(function (p) {
    /* widescreen card → tap opens the frame full screen (gallery-lightbox.js);
     * with a 4K wallpaper download (EGT-marked, data-wall). */
    return { n: "figure", c: "egt-exp__plate egt-exp-reveal", tabindex: "0", role: "button",
      "aria-label": "View full screen: " + p.cap,
      "data-full": "/imgs/exploit/" + p.img + ".jpg", "data-wall": "/imgs/exploit/wallpaper/exploit-" + p.img + ".jpg",
      i: [ { n: "img", c: "egt-exp__plate-img", src: "/imgs/exploit/" + p.img + ".jpg", alt: "", loading: "lazy", decoding: "async" },
        { n: "figcaption", c: "egt-exp__plate-cap", i: p.cap } ] }; });
  return { n: "section", c: "egt-exp__sec egt-exp__sec--17 egt-exp__dark", "data-exp-sec": "17",
    i: { n: "div", c: "egt__shell",
      i: [ { n: "div", c: "egt-exp__center", i: eyebrow("THE WORLD IN STILLS") },
        { n: "div", c: "egt-exp__gallery", i: plates } ] } };
}

/* ===================================================================
 * SOUNDTRACK — the album hero for "Exploit: Act 1" + the song→character-POV
 * liner notes, then the three-act structure. Founder, 2026-07-24: "add a hero
 * in the Exploit page too — add the which songs are designed for which
 * character's POV in a cool creative yet typography epic way."
 *
 * DATA is authoritative (task-tracker F35/F36 — not invented). The tracklist and
 * each track's in-world VOICE come from the ACT ONE table; the Apple / Spotify /
 * YouTube links are single-sourced from S.ALBUMS (id "exploit-act-1") so this
 * surface can never drift from home / entertainment.
 *
 * ⛔ TITLE RULE (F35 · Exploit decision #81): the album title is EXACTLY
 * "Exploit: Act 1". Never place an act NAME adjacent to "Exploit" as a noun it
 * could govern ("Exploit: The Child" reads as "exploit the child" — it forced a
 * DistroKid takedown). Act NUMBERS ("Act 1/2/3") are the safe, canonical form.
 *
 * COUPLING NOTES (task S1 diagnosis): armKinetic() (app/public/src/egt.exploit.js)
 * REBUILDS the textContent of .egt-exp__h / .egt-exp__excerpt-line / .egt-exp__pull
 * / .egt-exp__hook-line--beat into per-word spans — so track titles use their OWN
 * classes (.egt-exp__pov-title, .egt-exp__album-title …), never those four. Both
 * new sections carry data-exp-noindex so buildIndices() skips them and the
 * existing oversized numeral sequence (02, 03, …) is unchanged. No scrim on the
 * cover art (agents.md §4) — the covers are the media and they breathe. */
/* the three acts — Track counts are canon (13 / 14 / 19). The `id` maps each
 * card to its OWN album in the shared ALBUMS source, so a live act shows ITS
 * streaming links (not another act's). Live/Coming is DERIVED per render from
 * S.albumStreamable(actAlbum(id)) — never a hand-coded status — so this section
 * can never drift from /entertainment + the album detail pages (all three now
 * key off the same predicate). All three acts are streamable as of 2026-08-21;
 * an act flips itself the moment its album becomes streamable. */
var ACTS = [
  { id: "exploit-act-1", label: "Act 1", tracks: "13 songs", art: "/imgs/albums/exploit-act-1.jpg" },
  { id: "exploit-act-2", label: "Act 2", tracks: "14 songs", art: "/imgs/albums/exploit-act-2.jpg" },
  { id: "exploit-act-3", label: "Act 3", tracks: "19 songs", art: "/imgs/albums/exploit-act-3.jpg" }
];
function act1() { return S.ALBUMS.filter(function (x) { return x.id === "exploit-act-1"; })[0] || {}; }

/* the album lockup: the real cover art (breathing, undimmed) + title / subtitle /
 * out-now + the three icon-only Listen pills (S.streamLinks — icons only is a
 * founder rule; the service name lives in each pill's aria-label). */
function albumLockup() {
  var a = act1();
  return { n: "div", c: "egt-exp__album egt-exp-reveal",
    i: [
      { n: "div", c: "egt-exp__album-art",
        i: { n: "img", c: "egt-exp__album-cover", src: "/imgs/albums/exploit-act-1.jpg",
          alt: "Exploit: Act 1 — album cover", width: "700", height: "700",
          loading: "lazy", decoding: "async" } },
      { n: "div", c: "egt-exp__album-meta",
        i: [
          micro("Out now · 13 songs", "egt-exp__micro--label"),
          { n: "p", c: "egt-exp__album-title", i: "Exploit: Act 1" },
          { n: "p", c: "egt-exp__album-sub", i: "Songs from the novel · Book One of The Universe." },
          { n: "div", c: "egt-exp__album-listen",
            i: [ { n: "span", c: "egt-exp__album-listen-lbl", i: "Listen" },
              { n: "div", c: "egt-exp__streams", i: S.streamLinks(a.apple, a.spotify, a.youtube) } ] }
        ] }
    ] };
}

/* The three acts' tracklists, rendered behind a TABBED interface (Act 1 default)
 * so all three fit compactly under the soundtrack beat. Each panel is the SAME
 * shared row builder (TR.trackRow) the /entertainment album pages use — the
 * "Character:" POV + one-line feeling + the fs.existsSync-gated preview player —
 * reading the SINGLE ALBUMS source in egt.sections.js by id, so /exploit and the
 * album detail pages can never diverge. Client behaviour: the album preview
 * player (app/public/src/egt.album.js) + the accessible tab switcher
 * (app/public/src/egt.exploit.tabs.js), both loaded from exploit.json. */
var SOUNDTRACK_TABS = [
  { id: "exploit-act-1", label: "Act 1" },
  { id: "exploit-act-2", label: "Act 2" },
  { id: "exploit-act-3", label: "Act 3" }
];
function actAlbum(id) { return S.ALBUMS.filter(function (x) { return x.id === id; })[0] || {}; }
function soundtrackTabs() {
  var tabs = [], panels = [];
  SOUNDTRACK_TABS.forEach(function (act, idx) {
    var selected = idx === 0;
    var tabId = "exp-act-tab-" + (idx + 1);
    var panelId = "exp-act-panel-" + (idx + 1);
    tabs.push({ n: "button", type: "button",
      c: "egt-exp__act-tab" + (selected ? " is-selected" : ""),
      id: tabId, role: "tab",
      "aria-selected": selected ? "true" : "false",
      "aria-controls": panelId,
      tabindex: selected ? "0" : "-1",
      i: { n: "span", c: "egt-exp__act-tab-lbl", i: act.label } });
    var a = actAlbum(act.id);
    var rows = (a.tracks || []).map(function (t, i) { return TR.trackRow(a.id, t, i, a.explicit); });
    var panel = { n: "div", c: "egt-exp__act-panel",
      id: panelId, role: "tabpanel", "aria-labelledby": tabId, tabindex: "0",
      i: { n: "ol", c: "egt-album-trks", role: "list",
        "aria-label": a.nm + " tracklist", i: rows } };
    if (!selected) { panel.hidden = "hidden"; }
    panels.push(panel);
  });
  return { n: "div", c: "egt-exp__act-tabs", "data-exp-tabs": "true",
    i: [
      { n: "div", c: "egt-exp__act-tablist", role: "tablist",
        "aria-label": "Choose an act", i: tabs },
      { n: "div", c: "egt-exp__act-panels", i: panels }
    ] };
}

/* §17B — the soundtrack: album hero + the song→POV liner notes. Sits right after
 * the visual gallery (§17 "The world in stills") — the world in stills, then the
 * world in song — and before the first-readers beat. */
function secSoundtrack() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--album egt-exp__dark egt-exp__center",
    "data-exp-sec": "album", "data-exp-noindex": "true",
    i: { n: "div", c: "egt__shell",
      i: [
        eyebrow("THE SOUNDTRACK"),
        h("Every song, sung from inside the story."),
        body([ "Thirteen songs — ", { n: "em", i: "Exploit: Act 1" }, ", the first act of the score for the novel, out now. Each track is voiced in-world by the character who lives it." ]),
        albumLockup(),
        { n: "div", c: "egt-exp__gold-line egt-exp-reveal", "aria-hidden": "true" },
        micro("EVERY ACT · THE VOICES", "egt-exp__micro--label"),
        soundtrackTabs(),
        micro("Every song is sung by the character in whose voice it's written.", "egt-exp__micro--caption")
      ] } };
}

/* §17C — the three acts. All three are live as of 2026-08-21, each with Listen
 * pills for its OWN album; an act that is not yet streamable falls back to a
 * "Coming" badge with its real cover art and NO date (founder ruling).
 * Cross-links to /entertainment. */
function actCard(act) {
  var a = actAlbum(act.id);
  /* DERIVED, never hand-coded: an act is "live" only when its album is genuinely
   * streamable (released AND ≥1 link). This keeps /exploit in lockstep with
   * /entertainment + the detail page and is the safety net for Act 3 — the day
   * its date passes but before links land it stays "Coming"/"Not yet released"
   * (albumStreamable is false), never a contradiction. */
  var live = S.albumStreamable(a);
  var badge = { n: "span", c: "egt-exp__act-badge egt-exp__act-badge--" + (live ? "out" : "coming"),
    i: live ? "Out now" : "Coming" };
  var foot = live
    ? { n: "div", c: "egt-exp__streams egt-exp__act-streams", i: S.streamLinks(a.apple, a.spotify, a.youtube) }
    : { n: "p", c: "egt-exp__act-note", i: "Not yet released" };
  return { n: "article", c: "egt-exp__act egt-exp__act--" + (live ? "live" : "coming") + " egt-exp-reveal",
    i: [
      { n: "div", c: "egt-exp__act-art",
        i: { n: "img", c: "egt-exp__act-cover", src: act.art,
          alt: "Exploit: " + act.label + " — album cover", width: "700", height: "700",
          loading: "lazy", decoding: "async" } },
      { n: "div", c: "egt-exp__act-body",
        i: [
          { n: "div", c: "egt-exp__act-head",
            i: [ { n: "span", c: "egt-exp__act-label", i: act.label }, badge ] },
          { n: "p", c: "egt-exp__act-tracks", i: act.tracks },
          foot
        ] }
    ] };
}
function secActs() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--acts egt-exp__center",
    "data-exp-sec": "acts", "data-exp-noindex": "true",
    i: { n: "div", c: "egt__shell",
      i: [
        eyebrow("THE FULL SCORE"),
        h("Three acts. One score for the whole novel."),
        body([ { n: "em", i: "Exploit: Act 1" }, ", ", { n: "em", i: "Act 2" }, " and ", { n: "em", i: "Act 3" }, " are all out now — the complete score, written act by act alongside the book." ]),
        { n: "div", c: "egt-exp__acts", i: ACTS.map(actCard) },
        micro([ "Hear the whole catalogue at ", { n: "a", c: "egt-exp__link", href: "/entertainment", i: "EGT Entertainment" }, "." ], "egt-exp__micro--caption")
      ] } };
}

/* §18 — "The first readers" — REMOVED 2026-08-21. Its heading ("The record of
 * this world is still open") and its whole body were the registry of The Named:
 * a preorder-only exclusive that comes off the public page (founder ruling R2).
 * Nothing is left standing in its place — §19 below now carries the one thing
 * this beat was really for, which is "be here before everyone else". */

/* §19 — WHAT HAPPENS NEXT. This slot used to be the money section (the Named
 * Edition offer). Rewritten 2026-08-21: nothing is sold from this page, so the
 * beat that closes the story does the only two jobs left — say the book is
 * COMING SOON (no month, no year, no window: agents.md §4) and hand over the
 * free opening, which is the same act as joining the list.
 *
 * It KEEPS id="read" deliberately. That fragment is the `readUrl` emailed to
 * every reader who asks for the chapters (app/helpers/exploit.reserve.js) and
 * /src/egt.nav.js fires egt:reveal-sweep when a reader lands on it — dropping
 * the id would have silently pointed every one of those emails at the top of
 * the page. It also keeps the .egt-exp__author column and the --19 class so its
 * existing layout/typography rules still apply. */
function sec19() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--19 egt-exp__center", id: "read", "data-exp-sec": "19",
    i: { n: "div", c: "egt__shell egt-exp__author",
      i: [ eyebrow("WHAT HAPPENS NEXT"),
        { n: "h2", c: "egt-exp__h egt-exp__h--offer egt-exp-reveal", i: [ "The book is ", { n: "span", c: "egt-exp__gold", i: "coming soon." }, " The opening is yours now." ] },
        /* THIRD HEARING — it must ADD, never restate. §22 (high on the page)
         * already made the offer and the hero already named it; by the time a
         * reader reaches this beat they have been told twice. So this says the
         * thing neither of those does: exactly what arrives and how fast. Since
         * 2026-10-24 that is ONE email carrying a CODE: typing it verifies the
         * address, records consent AND opens the chapters on the page
         * (app/routes/egt.email.capture.js routeVerify). Do not re-import §22's
         * "enough of the story to know whether this world is yours" line; that
         * near-verbatim repeat is what this rewrite removed. */
        { n: "p", c: "egt-exp__body egt-exp-reveal", i: [ "One email, one code. Typing it confirms your address and opens the Foreword, the Prologue and Chapters 1–7 of ", { n: "em", i: "Exploit" }, " on the spot — there is no second message to wait for, nothing to buy, and no card asked for anywhere." ] },
        { n: "p", c: "egt-exp__body egt-exp-reveal", i: "After that the list stays quiet and useful: new chapters as they are released, the score as it drops, and the word the day the book is out. You can leave from any email." },
        { n: "div", c: "egt-exp__cta-row", i: ctaPrimary("Read the opening free") },
        micro("The ebook launches worldwide alongside the hardcover. No countdowns, no fake stock, no price games — you’ll simply hear it here first.", "egt-exp__micro--caption") ] } };
}

/* §20 — "Preorder only — it does not come back" — REMOVED 2026-08-21. It was
 * pure preorder scarcity: the flat price, the certificate, the registry, and
 * the closing window. All preorder-only, all off the public page (R2). Its one
 * line worth keeping — "no countdowns, no fake stock, no price games" — moved
 * into §19's closing caption, where it is now simply a description of the page. */

/* §21 (guarantee) intentionally removed: no money-back promise. */

/* §22 — THE FREE OPENING (email capture), source "free-chapter".
 *
 * CORRECTED 2026-08-21 — the note that used to sit here said this source lands
 * in an `exploit` segment while "footer" lands in `platform`. Neither is true
 * any more and had not been since the founder consolidated the list
 * (2026-07-29, commit 161f051). Verified against the code today:
 *   - app/helpers/egt.capture.js SOURCES — EVERY door, "free-chapter" and
 *     "footer" included, maps to the ONE `general` segment. There is no
 *     `exploit` segment and no `platform` segment to be filed under.
 *   - What still makes this capture special is the SOURCE LITERAL, not the
 *     segment. app/routes/egt.email.capture.js:403-413 gates the book delivery
 *     on FreeSample.promisedForSource(source), NOT on the segment, precisely
 *     because the segment is now `general` for everyone — the older
 *     promisedFor(segment) gate was permanently false and nobody got the book.
 *     "free-chapter" is in PROMISED_SOURCES (app/helpers/egt.free-sample.js:165)
 *     alongside exploit / album / ig / ig-swole, so it mints the sample.
 * So: keep the source literal EXACTLY as "free-chapter". Changing it to
 * something not in PROMISED_SOURCES silently stops the chapters being issued
 * while the signup still looks successful. The source is also what attributes
 * the signup on the founder's dashboard.
 *
 * Delivery IS immediate: the copy says "it's yours right away. Available now."
 * — do not re-introduce the old "arrives in a few days" hedge, which outlived
 * the delivery it described. No date on it either way (agents.md §4). The panel
 * is the shared frosted card over the section's own dark ground; no scrim, no
 * overlay.
 *
 * 2026-08-21: this section is now the ONLY ask on the surface. Every ctaPrimary
 * on the page, and the sticky dock, scroll the reader here. Treat it as the
 * conversion point of /exploit — if it moves, the anchor #read-the-opening
 * moves with it. */
function sec22() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--22 egt-exp__center egt-exp__dark", id: "read-the-opening", "data-exp-sec": "22",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("READ IT FIRST"),
        S.capture({
          source: "free-chapter",
          id: "egt-cap-exploit",
          variant: "panel",
          heading: "h2",
          title: [ "The opening of ", { n: "em", i: "Exploit" }, ", ", { n: "span", c: "egt-exp__gold", i: "free." } ],
          lead: "The Foreword, the Prologue and Chapters 1–7 — enough of the story to know whether this world is yours. Leave your address, confirm it, and it's yours right away. Available now.",
          label: "Your email address",
          cta: "Send me the opening",
          /* Q14 (founder, 2026-07-22): the inline form asks for the EMAIL and
           * nothing else. "How did you first hear about EGT?" is NOT here — it
           * moved into the post-submit dialog, which first tells the reader to
           * go confirm their address and only then asks the question, as a
           * bonus they may skip. Q15: the consent tick is required and ships
           * UNCHECKED (a pre-ticked box is not affirmative consent — GDPR
           * recital 32 names them explicitly, and double opt-in does not cure
           * it: confirming proves the address is real, not that the person
           * chose to subscribe). */
          consent: true,
          dialog: true,
          consentLabel: [ "Yes — email me the free opening of ", { n: "em", i: "Exploit" }, ", then new chapters, album drops and news from EGT. I can unsubscribe from any email." ],
          dialogLead: "",
          note: "You agree to the "
        })
        /* No trailing "costs nothing and commits you to nothing" caption —
         * removed at the founder's instruction 2026-07-22 (Q16). */
      ] } };
}

/* §24 — Final close (§23 FAQ lands just above it). */
function sec24() {
  return { n: "section", c: "egt-exp__sec egt-exp__sec--24 egt-exp__center egt-exp__close", "data-exp-sec": "24",
    i: { n: "div", c: "egt__shell",
      i: [ { n: "h2", c: "egt-exp__h egt-exp__h--hero egt-exp-reveal", i: "They came to break one man." },
        { n: "p", c: "egt-exp__sub egt-exp-reveal", i: "Be one of the hands reaching back." },
        { n: "div", c: "egt-exp__cta-row", i: ctaPrimary("Read the opening free") },
        micro("Book One of The Universe · a novel by Justin K Kazmierczak", "egt-exp__micro--trust") ] } };
}

/* §23 — FAQ (native details accordion). Single-sourced so the on-page accordion
 * AND the FAQPage JSON-LD (exploitSchema) never drift — schema.org's "answer
 * must match the visible answer" rule holds by construction.
 *
 * REWRITTEN 2026-08-21. The old array was four-fifths a sales FAQ: what a
 * preorder gets you, what The Named is, whether preordering is cheaper, when it
 * ships (which is how the ship DATE reached structured data), US-only shipping,
 * and whether to buy the ebook instead. None of it is true of this page any
 * more — nothing is sold here, nothing ships from here, and The Named is a
 * preorder-only exclusive that lives in the hidden wizard.
 *
 * What a reader actually needs answered now, and all this array does: what the
 * book is, how to read the opening free, when it comes out (COMING SOON — no
 * month, no year, no window), what the list gets them, and that the music is
 * free. Every answer is a fact already visible elsewhere on this page. Do NOT
 * add a date here: this array is the one path by which a date on this surface
 * would reach the FAQPage JSON-LD. */
var EXPLOIT_FAQ = [
  ["What is Exploit?", "Book One of The Universe — a cinematic spiritual thriller by Justin K Kazmierczak. An innocent man is marked by the oldest dark power in the world, and everyone he loves is taken to break him. What the order never counted on was the eight people who refuse to let him fall alone. It is a complete story you can start and finish on its own."],
  /* Deliberately does NOT reuse §22's "enough of the story to know whether this
   * world is yours" — that sentence earns its place once, in the panel, as the
   * offer. Here the job is procedural and short: how, and how long. (This array
   * is single-sourced into the FAQPage JSON-LD, so a duplicate here would ship
   * twice.) */
  ["How do I read the opening for free?", "Put your address in the panel on this page and confirm it — that is the whole process, and the chapters open the moment you do. It costs nothing."],
  ["When does the book come out?", "Coming soon. We are not putting a date on it until we can stand behind one. Get the free opening above and you are on the list — when the book lands, the people on that list are the ones we tell."],
  ["What does being on the list get me?", "The free opening first, then new chapters as they are released, the album drops, and the word the day the book is out. One list, and you can unsubscribe from any email."],
  ["Is the music really free?", "Yes. All three acts of the Exploit score are out now and free to stream for everyone — nothing to buy and nothing to join. Hear them at EGT Entertainment."],
  ["Will there be an ebook?", "Yes — the ebook launches worldwide alongside the hardcover."]
];
/* The answer is single-sourced from EXPLOIT_FAQ into BOTH this visible
 * <details> and the FAQPage JSON-LD at the bottom of the file, and schema.org
 * requires the two to match. S.priceToken only wraps the token in a span — the
 * rendered textContent is byte-identical to the raw string the JSON-LD emits,
 * so the pair still matches. Never fork the source array. */
function faqItem(q, a) {
  return { n: "details", c: "egt-exp__faq egt-exp-reveal",
    i: [ { n: "summary", c: "egt-exp__faq-q", i: q }, { n: "div", c: "egt-exp__faq-a", i: S.priceToken(a) } ] };
}
function sec23() {
  var items = EXPLOIT_FAQ.map(function (qa) { return faqItem(qa[0], qa[1]); });
  return { n: "section", c: "egt-exp__sec egt-exp__sec--23 egt-exp__center", "data-exp-sec": "23",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("QUESTIONS"),
        h("The honest answers."),
        { n: "div", c: "egt-exp__faqs", i: items } ] } };
}

/* Per-surface JSON-LD: WebPage + BreadcrumbList + Book + FAQPage for /exploit.
 * Every fact here is visibly on the page: the title "Exploit", the byline
 * "Justin K Kazmierczak" (already public in the codebase), the genre line "A
 * cinematic spiritual thriller", and the hardcover format.
 *
 * NO `offers` BLOCK — removed 2026-08-21 with the preorder. It declared
 * availability PreOrder, price 75 USD, a $7.99 US shipping rate and an offer
 * URL of /exploit/preorder: a live, machine-readable offer for something this
 * site no longer sells, pointing at a page that now 404s. A Book with no live
 * offer simply carries no Offer — that is the honest graph, and it is what
 * keeps Google/AI answers from quoting a price nobody can pay. If the book is
 * ever sold again, restore the block from git AND keep it byte-identical to the
 * one in egt.exploit.reserve.js (both emit @id /exploit#book; a divergence is a
 * self-contradicting graph).
 *
 * Still NO isbn (not shown) and still NO datePublished / releaseDate — the book
 * is "coming soon" and inventing a date here would put one in structured data
 * that appears nowhere on the page. Site-wide Organization + WebSite come from
 * S.page; URLs from S.siteOrigin(). */
function exploitSchema() {
  var O = S.siteOrigin();
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/exploit#webpage", url: O + "/exploit",
      name: "Exploit: Book One of The Universe",
      isPartOf: { "@id": O + "/#website" },
      about: { "@id": O + "/exploit#book" },
      breadcrumb: { "@id": O + "/exploit#breadcrumb" } },
    { "@type": "BreadcrumbList", "@id": O + "/exploit#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "Exploit: Book One of The Universe" }
      ] },
    /* Book only — NOT ["Book","Product"] (changed 2026-10-09, SEO wave 2B-1):
     * there is no offer, and Google's product snippets need offers / review /
     * aggregateRating, so the Product type asserted nothing true. `brand` was a
     * Product-only property and went with it; the Book binds to EGT through its
     * author (the founder Person, who worksFor #organization) and the page's
     * WebSite publisher. bookFormat stays pending the founder's edition ruling. */
    { "@type": "Book", "@id": O + "/exploit#book", name: "Exploit: Book One of The Universe",
      /* alternateName disambiguates the entity from the hacking/computer-security
       * "exploit" collision the bare word owns — so the book resolves for the
       * compounded queries readers actually use. No sameAs: the retailer /
       * Goodreads pages don't exist yet, and dead links are dishonest + useless. */
      alternateName: ["Exploit", "Exploit (novel)", "Exploit: Book One of The Universe"],
      author: { "@id": O + "/founder#person" },
      /* Both visibly on-page: "Book One of The Universe" and "A cinematic
       * spiritual thriller" (the genre micro-line). */
      isPartOf: { "@type": "BookSeries", "@id": O + "/exploit#series", name: "The Universe" },
      genre: "Spiritual thriller",
      inLanguage: "en", bookFormat: "https://schema.org/Hardcover",
      url: O + "/exploit",
      description: "Book One of The Universe — a cinematic spiritual thriller by Justin K Kazmierczak. The opening chapters are free to read now; the book is coming soon.",
      image: O + "/imgs/exploit/exploit-title-card.avif" },
    /* The author, by the founder page's Person @id (one entity site-wide);
     * name + url repeated so this page's graph resolves on its own. */
    { "@type": "Person", "@id": O + "/founder#person",
      name: "Justin K Kazmierczak", url: O + "/founder" },
    /* FAQPage from the SAME on-page §23 accordion (EXPLOIT_FAQ) — for AI-answer
     * extraction. FAQ rich results are retired (2026), so this is AI/on-page
     * value only; answers match the visible copy verbatim (single-sourced). */
    { "@type": "FAQPage", "@id": O + "/exploit#faq",
      mainEntity: EXPLOIT_FAQ.map(function (qa) {
        return { "@type": "Question", name: qa[0],
          acceptedAnswer: { "@type": "Answer", text: qa[1] } };
      }) }
  ]);
}

exports.render = async function render() {
  var content = { n: "div", c: "egt-exp", "data-exp-root": "true",
    /* §22 (the free-opening capture) sits UNDER §02 — directly after the hook.
     * Founder ruling 2026-07-22 (Q13). It used to sit below the first offer
     * (§04b) and, before that, down at §22's namesake position near the FAQ.
     * The reasoning that moved it up held then and is simply the whole page
     * now: the list is the asset and the book is the byproduct. With §04b and
     * the rest of the preorder architecture gone (2026-08-21), this section is
     * the ONE ask on the surface and everything else exists to earn it.
     *
     * The section keeps its §22 name so it still matches its definition and the
     * research notes — the number is its identity, not its position.
     *
     * §04b, §18 and §20 are gone with the preorder; §19 is now "what happens
     * next" and still carries id="read" (the emailed readUrl fragment). The
     * numbering the client paints (buildIndices) is derived from DOM order at
     * runtime, so it re-sequences itself and no numeral is hard-coded. */
    i: [ exploitSchema(), sec01(), secStats(), sec02(),
      sec22(),
      sec03(), sec04(),
      sec05(), sec06(), sec07(), sec08(), sec09(), sec10(),
      sec11(), sec12(), sec13(), sec14(), sec15(), sec16(), sec17(),
      secSoundtrack(), secActs(),
      sec19(), sec23(), sec24() ] };
  /* S.page adds the EGT nav + footer shell. The nav CTA is the free opening
   * (2026-08-21: there is no preorder to point at, and this surface has exactly
   * one ask). It links to /exploit/free — the standalone landing page for the
   * same offer — rather than the on-page anchor, so the sticky nav CTA reads
   * identically on every Exploit surface. No word-whoosh tease: that's
   * Platform-specific copy, gated to the unoverridden default CTA in nav()
   * (egt.sections.js). */
  /* Structural price-token integrity check (H18): every visible "$75 + shipping"
   * must render inside an egt-price-token span or this THROWS. There is no
   * price on this page any more, so it currently guards nothing — it is kept
   * deliberately, as the tripwire that fires the moment a price comes back
   * without its unbreakable span. The JSON-LD script node is skipped by design. */
  S.assertPriceTokensGuarded(content);
  /* hideFooterCapture — DESIGN FIX 2026-08-21. §24's closing CTA and the global
   * footer's "Be first to know / Notify me" strip landed in the same screen, so
   * the page ended on two email asks. They are not even the same ask: the footer
   * source is "footer", which does NOT mint the free chapters (PROMISED_SOURCES,
   * app/helpers/egt.free-sample.js), so the nearer box would have handed a
   * reader a newsletter instead of the book. This surface has ONE door — §22 —
   * and every CTA on it, including §24's, leads there. Opt-in and additive:
   * every other surface keeps its footer capture untouched. */
  return S.page("/exploit/free", [ content ], {
    navCta: { label: "Read the opening free", href: "/exploit/free" },
    hideFooterCapture: true
  });
};
